/**
 * Terms of Use / Privacy Policy content shown on the public
 * bassparlouradmin.com/terms-of-use and /privacy pages.
 *
 * Verbatim copy of the same legal text already bundled in the mobile app
 * (mobile/app/services/legal/legal.types.ts) — kept in sync manually since
 * the admin and mobile apps don't share code. The admin API endpoint these
 * pages used to depend on (`GET /api/v1/page/term-privacy`) was never
 * ported to the new backend, so these pages need their own copy to be a
 * functional public Terms of Use / Privacy Policy link for App Store review.
 */

export const TERMS = {
  title: "Terms of Service of The Bass Parlour App",
  effectiveDate: "Effective date: Nov 23, 2020",
  blocks: [
    {
      type: "paragraph",
      text: "The following Terms of Service (collectively, these “Terms of Service”) apply to your use of the Bass Parlour App, including any content, functionality and services offered on or via the Bass Parlour App.",
    },
    {
      type: "paragraph",
      text: "Please read the Terms of Service carefully before you start using The Bass Parlour App, because by using this mobile application you accept and agree to be bound and abide by these Terms of Service.",
    },
    {
      type: "paragraph",
      text: "These Terms of Service are effective immediately. We expressly reserve the right to change these Terms of Service from time to time without notice to you. You acknowledge and agree that it is your responsibility to review this mobile application and these Terms of Service from time to time and to familiarize yourself with any modifications. Your continued use of this mobile application after such modifications will constitute acknowledgement of the modified Terms and Conditions and agreement to abide and be bound by the modified Terms of Service.",
    },
    { type: "heading", text: "Conduct on the Mobile Application" },
    {
      type: "paragraph",
      text: "Your use of the mobile application is subject to all applicable laws and regulations, and you are solely responsible for the substance of your communications through the mobile application.",
    },
    {
      type: "paragraph",
      text: "By posting information in or otherwise using any interactive service that may be available to you on or through this mobile application, you agree that you will not upload, share, post, or otherwise distribute or facilitate distribution of any content — including text, communications, images, videos, sounds, data, or other information — that:",
    },
    {
      type: "list",
      items: [
        "Is unlawful, threatening, abusive, harassing, defamatory, libelous, deceptive, fraudulent, invasive of another's privacy, tortious, contains explicit or graphic descriptions or accounts of sexual acts (including but not limited to sexual language of a violent or threatening nature directed at another individual or group of individuals), or otherwise violates our rules or policies",
        "Victimizes, harasses, degrades, or intimidates an individual or group of individuals on the basis of religion, gender, sexual orientation, race, ethnicity, age, or disability",
        "Infringes on any patent, trademark, trade secret, copyright, right of publicity, or other proprietary right of any party",
        "Constitutes unauthorized or unsolicited advertising, junk or bulk email (also known as “spamming”), chain letters, any other form of unauthorized solicitation, or any form of lottery or gambling",
        "Contains software viruses or any other computer code, files, or programs that are designed or intended to disrupt, damage, or limit the functioning of any software, hardware, or telecommunications equipment or to damage or obtain unauthorized access to any data or other information of any third party",
        "Impersonates any person or entity, including any of our employees or representatives",
      ],
    },
    {
      type: "paragraph",
      text: "We neither endorse nor assume any liability for the contents of any material uploaded or submitted by third party users of the mobile application. We generally do not pre-screen, monitor, or edit the content posted by users of our interactive services that may be available on or through this mobile application.",
    },
    {
      type: "paragraph",
      text: "However, we and our agents have the right at their sole discretion to remove any content that, in our judgment, does not comply with these Terms of Use and any other rules of user conduct for our mobile application, or is otherwise harmful, objectionable, or inaccurate. We are not responsible for any failure or delay in removing such content. You hereby consent to such removal and waive any claim against us arising out of such removal of content.",
    },
    {
      type: "paragraph",
      text: "You agree that we may at any time, and at our sole discretion, terminate your membership, account, or other affiliation with our mobile application without prior notice to you for violating any of the above provisions.",
    },
    {
      type: "paragraph",
      text: "In addition, you acknowledge that we will cooperate fully with investigations of violations of systems or network security at other mobile application and/or sites, including cooperating with law enforcement authorities in investigating suspected criminal violations.",
    },
    { type: "heading", text: "Intellectual Property" },
    {
      type: "paragraph",
      text: "By accepting these Terms of Service, you acknowledge and agree that all content presented to you on this mobile application is protected by copyrights, trademarks, service marks, patents or other proprietary rights and laws.",
    },
    {
      type: "paragraph",
      text: "You are only permitted to use the content as expressly authorized by the specific content provider. You may not copy, reproduce, modify, republish, download, post, transmit, or distribute any photos, audio files, or information from this mobile application in any form or by any means without prior written permission from us or the specific content provider, and you are solely responsible for obtaining permission before reusing any copyrighted material that is available on this mobile application.",
    },
    { type: "heading", text: "Third Party Websites" },
    {
      type: "paragraph",
      text: "This mobile application may link you to other sites on the Internet or otherwise include references to information, documents, software, materials and/or services provided by other parties. These websites may contain information or material that some people may find inappropriate or offensive.",
    },
    {
      type: "paragraph",
      text: "These other websites and parties are not under our control, and you acknowledge that we are not responsible for the accuracy, copyright compliance, legality, decency, or any other aspect of the content of such sites, nor are we responsible for errors or omissions in any references to other parties or their products and services. The inclusion of such a link or reference is provided merely as a convenience and does not imply endorsement of, or association with, the Website or party by us, or any warranty of any kind, either express or implied.",
    },
    {
      type: "heading",
      text: "Disclaimer of Warranties, Limitations of Liability and Indemnification",
    },
    {
      type: "paragraph",
      text: "Your use of The Bass Parlour App is at your sole risk. The mobile application is provided “as is” and “as available”. We disclaim all warranties of any kind, express or implied, including, without limitation, the warranties of merchantability, fitness for a particular purpose and non-infringement.",
    },
    {
      type: "paragraph",
      text: "We are not liable for damages, direct or consequential, resulting from your use of the mobile application, and you agree to defend, indemnify and hold us harmless from any claims, losses, liability costs and expenses (including but not limited to attorney's fees) arising from your violation of any third-party's rights. You acknowledge that you have only a limited, non-exclusive, nontransferable license to use this mobile application. Because the mobile application is not error or bug free, you agree that you will use it carefully and avoid using it ways which might result in any loss of your or any third party's property or information.",
    },
    { type: "heading", text: "Term and termination" },
    {
      type: "paragraph",
      text: "This Terms of Service will become effective in relation to you when you create a Bass Parlour App account or when you start using the Bass Parlour App and will remain effective until terminated by us.",
    },
    {
      type: "paragraph",
      text: "The Bass Parlour App reserves the right to terminate this Terms of Service or suspend your account at any time in case of unauthorized, or suspected unauthorized use of the mobile application whether in contravention of this Terms of Service or otherwise. If the Bass Parlour App terminates this Terms of Service, or suspends your account for any of the reasons set out in this section, the Bass Parlour App shall have no liability or responsibility to you.",
    },
    { type: "heading", text: "Assignment" },
    {
      type: "paragraph",
      text: "The Bass Parlour App may assign this Terms of Service or any part of it without restrictions. You may not assign this Terms of Service or any part of it to any third party.",
    },
    { type: "heading", text: "Governing Law" },
    {
      type: "paragraph",
      text: "These Terms of Service and any dispute or claim arising out of, or related to them, shall be governed by and construed in accordance with the internal laws of the us without giving effect to any choice or conflict of law provision or rule.",
    },
    {
      type: "paragraph",
      text: "Any legal suit, action or proceeding arising out of, or related to, these Terms of Service or the mobile application shall be instituted exclusively in the federal courts of us.",
    },
  ],
}

export const PRIVACY = {
  title: "Privacy Policy",
  effectiveDate: "Effective date: Nov 23, 2020",
  blocks: [
    {
      type: "paragraph",
      text: "Bass Parlour, LLC (“us”, “we”, or “our”) operates the Bass Parlour mobile application (hereinafter referred to as the “Service”).",
    },
    {
      type: "paragraph",
      text: "This page informs you of our policies regarding the collection, use and disclosure of personal data when you use our Service and the choices you have associated with that data.",
    },
    {
      type: "paragraph",
      text: "We use your data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this policy. Unless otherwise defined in this Privacy Policy, the terms used in this Privacy Policy have the same meanings as in our Terms and Conditions.",
    },
    {
      type: "paragraph",
      text: "We built the Bass Parlour App as a Freemium app. This mobile application is provided by us at no cost and is intended for use as is. If you choose to use our Service, then you agree to the collection and use of information in relation to this policy. The Personal Information that we collect is used for providing and improving the Service. We will not use or share your information with anyone except as described in this Privacy Policy.",
    },
    {
      type: "paragraph",
      text: "The terms used in this Privacy Policy have the same meanings as in our Terms of Service, which is accessible at Bass Parlour App unless otherwise defined in this Privacy Policy.",
    },
    { type: "heading", text: "Information Collection and Use" },
    {
      type: "paragraph",
      text: "For a better experience, while using our Service, we may require you to provide us with certain personally identifiable information, including but not limited to name, email, phone number, location, and background information. The information that we request will be retained by us and used as described in this privacy policy.",
    },
    { type: "heading", text: "Log Data" },
    {
      type: "paragraph",
      text: "We want to inform you that whenever you use our Service, in a case of an error in the app we collect data and information (through third party products) on your phone called Log Data. This Log Data may include information such as your device Internet Protocol (“IP”) address, device name, operating system version, the configuration of the app when utilizing our Service, the time and date of your use of the Service, and other statistics.",
    },
    { type: "heading", text: "Cookies" },
    {
      type: "paragraph",
      text: "Cookies are files with a small amount of data that are commonly used as anonymous unique identifiers. These are sent to your browser from the websites that you visit and are stored on your device's internal memory.",
    },
    {
      type: "paragraph",
      text: "This Service does not use these “cookies” explicitly. However, the app may use third party code and libraries that use “cookies” to collect information and improve their services. You have the option to either accept or refuse these cookies and know when a cookie is being sent to your device. If you choose to refuse our cookies, you may not be able to use some portions of this Service.",
    },
    { type: "heading", text: "Service Providers" },
    {
      type: "paragraph",
      text: "We may employ third-party companies and individuals due to the following reasons:",
    },
    {
      type: "list",
      items: [
        "To facilitate our Service;",
        "To provide the Service on our behalf;",
        "To perform Service-related services; or",
        "To assist us in analyzing how our Service is used.",
      ],
    },
    {
      type: "paragraph",
      text: "We want to inform users of this Service that these third parties have access to your Personal Information. The reason is to perform the tasks assigned to them on our behalf. However, they are obligated not to disclose or use the information for any other purpose.",
    },
    { type: "heading", text: "Security" },
    {
      type: "paragraph",
      text: "We value your trust in providing us your Personal Information, thus we are striving to use commercially acceptable means of protecting it. But remember that no method of transmission over the internet, or method of electronic storage is 100% secure and reliable, and we cannot guarantee its absolute security.",
    },
    { type: "heading", text: "Bass Parlour App - Copyright Protection Measures" },
    {
      type: "list",
      items: [
        "The Bass Parlour App ‘Terms of Service’ stipulate that all app users of the Bass Parlour app are only permitted to upload music that they created or own.",
        "Our team will regularly browse each app member's profile for any music that was uploaded without ownership of that music.",
        "Bass Parlour App users can easily ‘report’ (to our admin team) any other user that they believe uploaded digital content that he/she does not own or did not create.",
      ],
    },
    {
      type: "paragraph",
      text: "Our App doesn't involve any exchange of funds between members, nor does our app involve any digital goods or products.",
    },
    { type: "heading", text: "Payments" },
    {
      type: "paragraph",
      text: "We will not store or collect your payment card details. That information is provided directly to our third-party payment processors whose use of your personal information is governed by their Privacy Policy. These payment processors adhere to the standards set by PCI-DSS as managed by the PCI Security Standards Council, which is a joint effort of brands like Visa, MasterCard, American Express and Discover. PCI-DSS requirements help ensure the secure handling of payment information.",
    },
    { type: "paragraph", text: "The payment processors we work with are:" },
    { type: "subheading", text: "Apple Store In-App Payments" },
    {
      type: "paragraph",
      text: "Their Privacy Policy can be viewed at https://www.apple.com/legal/privacy/en-ww/.",
    },
    { type: "subheading", text: "Google Play In-App Payments" },
    {
      type: "paragraph",
      text: "Their Privacy Policy can be viewed at https://www.google.com/policies/privacy/.",
    },
    { type: "heading", text: "Links to Other Sites" },
    {
      type: "paragraph",
      text: "This Service may contain links to other sites. If you click on a third-party link, you will be directed to that site. Note that these external sites are not operated by us. Therefore, we strongly advise you to review the Privacy Policy of these websites. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.",
    },
    { type: "heading", text: "Children's Privacy" },
    {
      type: "paragraph",
      text: "These Services do not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13. In the case we discover that a child under 13 has provided us with personal information, we immediately delete this from our servers. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us so that we will be able to do necessary actions.",
    },
    { type: "heading", text: "Camera And Audio Permission" },
    {
      type: "paragraph",
      text: "The app will need access to the camera to click image for end user that will be shown on their profile as user data and record introduction video to describe about themselves.",
    },
    {
      type: "paragraph",
      text: "The app will need access to microphone to record the audio while creating introduction videos.",
    },
    { type: "heading", text: "Changes to This Privacy Policy" },
    {
      type: "paragraph",
      text: "We may update our Privacy Policy from time to time. Thus, you are advised to review this page periodically for any changes. We will notify you of any changes by posting the new Privacy Policy on this page.",
    },
    { type: "paragraph", text: "This policy is effective as of 2020-09-01." },
    { type: "heading", text: "Contact Us" },
    {
      type: "paragraph",
      text: "If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us at info@bassparlour.com.",
    },
  ],
}
